/**
 * Bulk camera upload — a CSV in, one camera per row out.
 *
 * ─── THE FORMAT IS FETCHED, NOT WRITTEN DOWN HERE ───
 *
 * The column table below is rendered from `GET /cameras/import/spec`, and the template download
 * is the file the same service generates. Documenting the format in the frontend would mean two
 * descriptions of one contract, and the one an operator reads would be the one nobody updates.
 *
 * ─── THE ANSWER IS PER ROW, SO THE SCREEN IS TOO ───
 *
 * An import of twenty cameras with a typo in row 14 imports nineteen cameras. That is the whole
 * design of the endpoint, and it is worth nothing if the page reports "failed" or shows a toast:
 * the operator needs to know WHICH line, WHY, and that the other nineteen are already in. So the
 * result is a table keyed by the line number in their own spreadsheet, and a file that imported
 * nothing (HTTP 422) renders through exactly the same table as one that imported everything.
 */
import { useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  TableCellsIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import StatusTag from '../components/common/StatusTag';
import { Callout } from '../components/common/Callout';
import { usePermissions } from '../auth';
import { useApi } from '../api/ApiProvider';
import type { CameraImportOutcome, CameraImportSpec } from '../api/contracts';
import { parseCsv, normaliseHeader } from '../utils/parseCsv';
import { parseXlsx, canPreviewXlsx } from '../utils/parseXlsx';
import { PageTitle } from '../components/layout/TitleBar';

/** Card shell, so the three stages of the screen line up. */
function Panel({
  title, description, icon: Icon, actions = null, children,
}: {
  title: string;
  description?: string;
  icon?: ComponentType<{ className?: string }>;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-start gap-2.5">
          {Icon && (
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-light">
              <Icon className="h-4 w-4 text-brand" />
            </span>
          )}
          <div className="min-w-0">
            <h2 className="text-[14px] font-semibold text-foreground">{title}</h2>
            {description && <p className="mt-0.5 text-[12px] text-muted-foreground">{description}</p>}
          </div>
        </div>
        {actions}
      </header>
      <div className="space-y-4 p-4">{children}</div>
    </section>
  );
}

function CountTile({ label, value, tone = null }: { label: string; value: number; tone?: string | null }) {
  return (
    <div className="rounded-xl border border-border bg-background p-3.5">
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p
        className="mt-1.5 text-[20px] font-semibold tabular-nums text-foreground"
        style={tone ? { color: tone } : undefined}
      >
        {value}
      </p>
    </div>
  );
}

const STATUS_TONE = { created: 'success', skipped: 'warning', failed: 'danger' } as const;

export interface CameraImportPageProps {
  /**
   * Which capability lets this user import. The two apps spell it differently — the node calls
   * it `cameras.create`, the cloud `cameras.add` — and hard-coding either would silently hide
   * the button for half the operators who are entitled to it.
   */
  permission?: string;
  /** Where "back" and "view cameras" go. */
  camerasPath?: string;
  /** Named in the header on the cloud, so it is clear WHICH site the cameras land on. */
  siteName?: string | null;
}

export default function CameraImportPage({
  permission = 'cameras.create',
  camerasPath = '/cameras',
  siteName = null,
}: CameraImportPageProps) {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const { cameraImport } = useApi();
  const canImport = hasPermission(permission);

  const [spec, setSpec] = useState<CameraImportSpec | null>(null);
  const [specError, setSpecError] = useState('');

  const fileInput = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  interface Preview {
    headers: string[];
    /** Which tab a workbook's rows came from — null for a CSV, which has none. */
    sheet: string | null;
    recognised: string[];
    ignored: string[];
    rows: string[][];
  }
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState('');
  /**
   * Not an error — the file is fine, it just cannot be previewed in a browser.
   *
   * It has its own state because `previewError` DISABLES the Import button, and an .xlsx that
   * the server reads perfectly well must not be the reason the button is dead. That was the
   * whole of the "stuck after choosing a file" report.
   */
  const [previewNote, setPreviewNote] = useState('');
  const [dragging, setDragging] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<CameraImportOutcome | null>(null);

  useEffect(() => {
    let live = true;
    cameraImport!
      .spec()
      .then((s) => { if (live) setSpec(s); })
      .catch((err) => {
        if (live) setSpecError(err.response?.data?.message || 'Could not load the CSV format.');
      });
    return () => { live = false; };
  }, []);

  const knownColumns = useMemo(
    () => (spec?.columns || []).map((c) => c.name),
    [spec],
  );

  /**
   * A local look at the file before it is sent — row count, which columns were recognised, and
   * the first few rows as Trinetra will read them. The server parses it again and its verdict is
   * the one that counts; this exists so an operator does not upload a file with its columns in
   * the wrong place, or the wrong file entirely, and learn about it one round trip later.
   *
   * WORKBOOKS ARE READ HERE TOO. They did not use to be: an .xlsx was accepted with a note and
   * no preview, so the screen sat there looking inert while actually being ready, and a file
   * that was never a camera list got found out only after uploading. `parseXlsx` unzips it with
   * the platform's own inflater — no parser shipped into the bundle — and falls back to the note
   * on an engine that cannot, because the server can still read it.
   */
  function applyRows(rows: string[][], sheet: string | null) {
    const headers = rows[0].map(normaliseHeader);
    setPreview({
      headers,
      sheet,
      recognised: headers.filter((h) => knownColumns.includes(h)),
      ignored: headers.filter((h) => h && !knownColumns.includes(h)),
      rows: rows.slice(1),
    });
  }

  async function readPreview(chosen: File) {
    setPreviewError('');
    setPreviewNote('');
    setResult(null);
    setFile(chosen);
    setPreview(null);

    // .xls is the pre-2007 binary format — not a zip of XML, so neither side can read it.
    if (/\.xls$/i.test(chosen.name)) {
      setPreviewError(
        'That is the old .xls format. Open it in Excel and save as .xlsx (or .csv), then upload that.',
      );
      return;
    }

    if (/\.xlsx$/i.test(chosen.name)) {
      if (!canPreviewXlsx()) {
        setPreviewNote(
          'Excel workbook — this browser cannot open one for preview, but the server reads it '
          + 'when you import.',
        );
        return;
      }
      try {
        const { rows, sheetName } = await parseXlsx(await chosen.arrayBuffer());
        if (rows.length < 2) {
          setPreviewError(
            `That workbook${sheetName ? ` (sheet "${sheetName}")` : ''} has no rows under its `
            + 'header line.',
          );
          return;
        }
        applyRows(rows, sheetName);
      } catch (err: any) {
        setPreviewNote(
          `This workbook could not be opened for preview (${err.message}) — the server will try `
          + 'again when you import.',
        );
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const rows = parseCsv(String(reader.result || ''));
      if (rows.length < 2) {
        setPreviewError('That file has no camera rows under its header line.');
        return;
      }
      applyRows(rows, null);
    };
    reader.onerror = () => setPreviewError('That file could not be read.');
    reader.readAsText(chosen);
  }

  /**
   * The template, built from the spec this page already holds.
   *
   * Not a second endpoint: the cloud reaches its node through a JSON tunnel that cannot stream a
   * file download, and a template that existed on one app and not the other would be the first
   * thing to go missing.
   */
  function downloadTemplate() {
    if (!spec?.template_csv) return;
    // The BOM keeps Excel from mangling the em dash in the example row on open.
    const blob = new Blob([`\uFEFF${spec.template_csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'trinetra-cameras-template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function upload() {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setResult(null);
    // `importCsv` never throws: a file full of bad rows is a normal outcome here and its
    // per-row reasons are the answer, not an exception.
    const outcome = await cameraImport!.upload(file, setProgress);
    setResult(outcome);
    setUploading(false);
  }

  const missingRequired = preview
    ? (spec?.required_columns || []).filter((c) => !preview.headers.includes(c))
    : [];

  return (
    <div className="space-y-3">
      <PageTitle
        title={(
          <>
            <Link to={camerasPath} aria-label="Back to cameras" className="mr-1.5 inline-flex rounded p-0.5 align-[-3px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
              <ArrowLeftIcon className="h-4 w-4" />
            </Link>
            {siteName ? `Bulk camera upload — ${siteName}` : 'Bulk camera upload'}
          </>
        )}
        subtitle="Each .csv / .xlsx row becomes a camera, exactly as if added through the form"
      />

      {!canImport && (
        <Callout tone="warning">
          Your role cannot add cameras, so this file can be checked here but not imported.
        </Callout>
      )}

      {/* ── 1. The format ── */}
      <Panel
        title="File format"
        description="A .csv or an .xlsx workbook. First line is the header; column order does not matter, and unknown columns are ignored."
        icon={TableCellsIcon}
        actions={
          <button
            onClick={downloadTemplate}
            disabled={!spec}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4" /> Download template
          </button>
        }
      >
        {specError && <Callout tone="danger">{specError}</Callout>}

        {!spec && !specError && (
          <p className="text-[12px] text-muted-foreground">Loading the format…</p>
        )}

        {spec && (
          <>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-left text-[12px]">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-3 py-2 font-medium text-muted-foreground">Column</th>
                    <th className="px-3 py-2 font-medium text-muted-foreground">Required</th>
                    <th className="px-3 py-2 font-medium text-muted-foreground">What it is</th>
                    <th className="px-3 py-2 font-medium text-muted-foreground">Example</th>
                  </tr>
                </thead>
                <tbody>
                  {spec.columns.map((c) => (
                    <tr key={c.name} className="border-t border-border align-top">
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-foreground">{c.name}</td>
                      <td className="px-3 py-2">
                        {c.required
                          ? <StatusTag tone="brand">Required</StatusTag>
                          : <span className="text-muted-foreground">Optional</span>}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{c.description}</td>
                      <td className="max-w-[260px] truncate px-3 py-2 font-mono text-muted-foreground" title={c.example}>
                        {c.example || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="space-y-1">
              {spec.notes.map((note) => (
                <li key={note} className="flex gap-2 text-[12px] text-muted-foreground">
                  <span className="text-brand">•</span>
                  <span>{note}</span>
                </li>
              ))}
            </ul>

            <div className="rounded-lg border border-border bg-background">
              <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                <DocumentTextIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-[11px] font-medium text-muted-foreground">
                  Example file — up to {spec.max_rows} rows per upload
                </span>
              </div>
              <pre className="overflow-x-auto p-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
{spec.template_csv.trim()}
              </pre>
            </div>
          </>
        )}
      </Panel>

      {/* ── 2. The file ── */}
      <Panel
        title="Upload the file"
        description="Checked here first, then read again by the server — which is the verdict that counts."
        icon={ArrowUpTrayIcon}
      >
        <input
          ref={fileInput}
          type="file"
          accept=".csv,text/csv,.txt,.xlsx"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void readPreview(f);
            e.target.value = '';
          }}
        />

        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const f = e.dataTransfer.files?.[0];
            if (f) void readPreview(f);
          }}
        >
          <button
            onClick={() => fileInput.current?.click()}
            className={`flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-9 transition-colors ${
              dragging ? 'border-brand bg-brand-light' : 'border-border hover:border-brand hover:bg-muted'
            }`}
          >
            <ArrowUpTrayIcon className="h-6 w-6 text-muted-foreground" />
            <span className="text-[13px] font-medium text-foreground">
              {file ? file.name : 'Choose a CSV or Excel file, or drop one here'}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {file ? `${(file.size / 1024).toFixed(1)} KB` : '.csv or .xlsx, up to 2 MB'}
            </span>
          </button>
        </div>

        {previewError && <Callout tone="danger">{previewError}</Callout>}
        {previewNote && <Callout tone="info">{previewNote}</Callout>}

        {preview && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusTag tone="info">{preview.rows.length} rows</StatusTag>
              {preview.sheet && <StatusTag tone="neutral">sheet: {preview.sheet}</StatusTag>}
              <StatusTag tone="neutral">{preview.recognised.length} columns recognised</StatusTag>
              {preview.ignored.length > 0 && (
                <StatusTag tone="warning">
                  ignoring {preview.ignored.join(', ')}
                </StatusTag>
              )}
            </div>

            {missingRequired.length > 0 && (
              <Callout tone="danger">
                This file has no {missingRequired.map((c) => `"${c}"`).join(' or ')} column, so the
                server will refuse it. Rename the column in your sheet, or start from the template.
              </Callout>
            )}

            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-left text-[12px]">
                <thead className="bg-muted">
                  <tr>
                    {preview.headers.map((h, i) => (
                      <th
                        key={`${h}-${i}`}
                        className={`whitespace-nowrap px-3 py-2 font-medium ${
                          knownColumns.includes(h) ? 'text-foreground' : 'text-muted-foreground line-through'
                        }`}
                      >
                        {h || '—'}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.slice(0, 5).map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      {preview.headers.map((h, c) => (
                        <td
                          key={`${h}-${c}`}
                          className="max-w-[220px] truncate px-3 py-2 text-muted-foreground"
                          title={r[c]}
                        >
                          {r[c] || '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.rows.length > 5 && (
              <p className="text-[11px] text-muted-foreground">
                Showing the first 5 of {preview.rows.length} rows.
              </p>
            )}
          </>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <p className="text-[12px] text-muted-foreground">
            Imported cameras start streaming immediately. A row whose stream URL is already
            configured is skipped, never duplicated.
          </p>
          <button
            onClick={upload}
            disabled={!file || !canImport || uploading || !!previewError}
            className="rounded-lg bg-brand px-5 py-2 text-[13px] font-medium text-white transition-colors hover:bg-brand-dark disabled:opacity-50"
          >
            {uploading
              ? `Importing… ${progress}%`
              : `Import${preview ? ` ${preview.rows.length} rows` : ''}`}
          </button>
        </div>
      </Panel>

      {/* ── 3. The verdict ── */}
      {result && (
        <Panel
          title="Import result"
          description={result.message}
          icon={VideoCameraIcon}
          actions={
            result.summary.created > 0 && (
              <button
                onClick={() => navigate(camerasPath)}
                className="rounded-lg border border-border px-3 py-1.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted"
              >
                View cameras
              </button>
            )
          }
        >
          {!result.ok && result.rows.length === 0 && (
            <Callout tone="danger">
              {result.message}
              {result.details.length > 0 && (
                <ul className="mt-1.5 space-y-0.5">
                  {result.details.map((d, i) => (
                    <li key={i}>{d.message}</li>
                  ))}
                </ul>
              )}
            </Callout>
          )}

          {result.rows.length > 0 && (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <CountTile label="Rows in file" value={result.summary.total} />
                <CountTile label="Created" value={result.summary.created} tone="var(--success)" />
                <CountTile label="Skipped" value={result.summary.skipped} tone="var(--warning)" />
                <CountTile label="Failed" value={result.summary.failed} tone="var(--destructive)" />
              </div>

              {result.summary.ignored_columns?.length > 0 && (
                <Callout tone="info">
                  Columns Trinetra does not use were ignored:{' '}
                  <span className="font-mono">{result.summary.ignored_columns.join(', ')}</span>
                </Callout>
              )}

              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-left text-[12px]">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-3 py-2 font-medium text-muted-foreground">Line</th>
                      <th className="px-3 py-2 font-medium text-muted-foreground">Camera</th>
                      <th className="px-3 py-2 font-medium text-muted-foreground">Result</th>
                      <th className="px-3 py-2 font-medium text-muted-foreground">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r) => (
                      <tr key={`${r.line}-${r.name}`} className="border-t border-border align-top">
                        <td className="px-3 py-2 tabular-nums text-muted-foreground">{r.line}</td>
                        <td className="max-w-[240px] truncate px-3 py-2 text-foreground" title={r.name}>
                          {r.name}
                        </td>
                        <td className="px-3 py-2">
                          <StatusTag tone={STATUS_TONE[r.status] || 'neutral'}>{r.status}</StatusTag>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{r.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Panel>
      )}
    </div>
  );
}
